// eslint-disable-next-line no-unused-vars
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Checkbox,
  Row,
  Col,
  Table,
  Empty,
  Tooltip,
  message,
  Space,
  Modal,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import "./style.scss";
import Heading from "../../components/Heading";
import PincodeInput from "./PincodeInput";
import ServiceabilityListTable from "./ServiceabilityListTable";
import PincodeApi from "../../api/PincodeApi";

function VayuQuickPincodeMapping() {
  const [sourcePincodes, setSourcePincodes] = useState([]);
  const [destinationPincodes, setDestinationPincodes] = useState([]);
  const [vayuQuick, setVayuQuick] = useState(false);
  const [vayuQuickPro, setVayuQuickPro] = useState(false);
  const [isBookingOnly, setIsBookingOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch serviceability list
  useEffect(() => {
    fetchServiceabilityList(currentPage, pageSize);
  }, []);

  // Handle source pincode change
  const handleSourcePincodeChange = (pincodes, isBookingOnly) => {
    // First filter out any pincodes that already exist in destination
    const filteredPincodes = pincodes.filter(
      (pincode) => !destinationPincodes.includes(pincode)
    );

    // Check if there are duplicates
    if (filteredPincodes.length < pincodes.length) {
      const duplicatePincodes = pincodes.filter((pincode) =>
        destinationPincodes.includes(pincode)
      );
      message.warning({
        content: `${duplicatePincodes.length} pincode(s) already exist in destination pincodes and were not added.`,
        key: "pincode-warning",
        duration: 2,
      });
    }

    // If there are pincodes to validate
    if (filteredPincodes.length > 0) {
      // Show loading message
      const loadingMessage = message.loading({
        content: "Validating pincodes...",
        key: "pincode-validation",
        duration: 0,
      });

      // Prepare validation payload
      const validationPayload = {
        target: "SOURCE",
        pincodes: filteredPincodes,
      };

      // Call validation API
      PincodeApi.validateVayuQuickPincodeServiceability(validationPayload)
        .then(({ response, status }) => {
          loadingMessage();

          if (status && response.data && response.data.status === 200) {
            // Filter valid pincodes
            const validPincodes = response.data.data.filter(
              (item) => item.isValid
            );

            // Check if any pincodes were invalid
            if (validPincodes.length < filteredPincodes.length) {
              const invalidCount =
                filteredPincodes.length - validPincodes.length;
              message.warning({
                content: `${invalidCount} pincode(s) are invalid and were not added.`,
                key: "pincode-warning",
                duration: 2,
              });
            }

            // Only show success if we have valid pincodes
            if (validPincodes.length > 0) {
              setSourcePincodes([...sourcePincodes, ...validPincodes]);
              setIsBookingOnly(isBookingOnly);
              message.success({
                content: "Pincode added successfully",
                key: "pincode-success",
                duration: 2,
              });
            }
          } else {
            const errorMsg =
              response?.data?.message || "Failed to validate pincodes";
            message.error({
              content: errorMsg,
              key: "pincode-error",
              duration: 2,
            });
          }
        })
        .catch((error) => {
          loadingMessage();
          const errorMsg =
            error?.response?.data?.message || "Failed to validate pincodes";
          message.error({
            content: errorMsg,
            key: "pincode-error",
            duration: 2,
          });
        });
    }
  };

  // Handle destination pincode change
  const handleDestinationPincodeChange = (pincodes) => {
    // First filter out any pincodes that already exist in source
    const filteredPincodes = pincodes.filter(
      (pincode) => !sourcePincodes.includes(pincode)
    );

    // Check if there are duplicates
    if (filteredPincodes.length < pincodes.length) {
      const duplicatePincodes = pincodes.filter((pincode) =>
        sourcePincodes.includes(pincode)
      );
      message.warning({
        content: `${duplicatePincodes.length} pincode(s) already exist in source pincodes and were not added.`,
        key: "pincode-warning",
        duration: 2,
      });
    }

    // If there are pincodes to validate
    if (filteredPincodes.length > 0) {
      // Show loading message
      const loadingMessage = message.loading({
        content: "Validating pincodes...",
        key: "pincode-validation",
        duration: 0,
      });

      // Prepare validation payload
      const validationPayload = {
        target: "DESTINATION",
        pincodes: filteredPincodes,
      };

      // Call validation API
      PincodeApi.validateVayuQuickPincodeServiceability(validationPayload)
        .then(({ response, status }) => {
          loadingMessage();

          if (status && response.data && response.data.status === 200) {
            // Filter valid pincodes
            const validPincodes = response.data.data.filter(
              (item) => item.isValid
            );

            // Check if any pincodes were invalid
            if (validPincodes.length < filteredPincodes.length) {
              const invalidCount =
                filteredPincodes.length - validPincodes.length;
              message.warning({
                content: `${invalidCount} pincode(s) are invalid and were not added.`,
                key: "pincode-warning",
                duration: 2,
              });
            }

            // Only show success if we have valid pincodes
            if (validPincodes.length > 0) {
              setDestinationPincodes([
                ...destinationPincodes,
                ...validPincodes,
              ]);
              message.success({
                content: "Pincode added successfully",
                key: "pincode-success",
                duration: 2,
              });
            }
          } else {
            const errorMsg =
              response?.data?.message || "Failed to validate pincodes";
            message.error({
              content: errorMsg,
              key: "pincode-error",
              duration: 2,
            });
          }
        })
        .catch((error) => {
          loadingMessage();
          const errorMsg =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to validate pincodes";
          message.error({
            content: errorMsg,
            key: "pincode-error",
            duration: 2,
          });
        });
    }
  };

  const handlePageChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    fetchServiceabilityList(pagination.current, pagination.pageSize);
  };

  // Handle removing a source pincode
  const handleRemoveSourcePincode = (pincode) => {
    const newPincodes = sourcePincodes.filter((e) => e.pincode !== pincode);
    setSourcePincodes(newPincodes);
    message.success(`Pincode ${pincode} removed from source pincodes`);
  };

  // Handle removing a destination pincode
  const handleRemoveDestinationPincode = (pincode) => {
    const newPincodes = destinationPincodes.filter(
      (e) => e.pincode !== pincode
    );
    setDestinationPincodes(newPincodes);
    message.success(`Pincode ${pincode} removed from destination pincodes`);
  };

  // Validation function to determine if save button should be disabled
  const isSaveDisabled = () => {
    // Check if at least one service type is selected
    const isServiceSelected = vayuQuick || vayuQuickPro;

    // Check if both source and destination have at least one pincode
    const hasPincodes =
      sourcePincodes.length > 0 && destinationPincodes.length > 0;

    // Button should be disabled if either condition is not met
    return !isServiceSelected || !hasPincodes;
  };

  const handleSave = () => {
    if (isSaveDisabled()) {
      if (!vayuQuick && !vayuQuickPro) {
        message.error(
          "Please select at least one service type: VayuQuick or VayuQuick Pro"
        );
      }
      if (sourcePincodes.length === 0) {
        message.error("Please add at least one source pincode");
      }
      if (destinationPincodes.length === 0) {
        message.error("Please add at least one destination pincode");
      }
      return;
    }

    // Show confirmation modal based on isBookingOnly state
    Modal.confirm({
      title: "Confirm Action",
      content: isBookingOnly
        ? "Are you sure you want to enable the pincodes with booking only?"
        : "Are you sure you want to enable booking and delivery on these pincodes?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        // Show loading message
        const loadingMessage = message.loading({
          content: "Saving pincode serviceability...",
          key: "saveLoading",
          duration: 0,
        });

        const mappedSourcePincodes = sourcePincodes.map((e) => e.pincode);
        const mappedDestinationPincodes = destinationPincodes.map((e) => e.pincode);

        // Prepare request payload
        const payload = {
          vayuquick: vayuQuick,
          vayuquickPro: vayuQuickPro,
          isBookingOnly: isBookingOnly,
          sourcePincodes: mappedSourcePincodes,
          destinationPincodes: mappedDestinationPincodes,
        };

        // Make API call using the pre-existing API methods
        PincodeApi.createVayuQuickPincodeServiceability(payload)
          .then(({ response, status }) => {
            loadingMessage();

            if (status && response.data && response.data.status === 200) {
              message.success({
                content: "Pincode serviceability saved successfully",
                key: "saveLoading",
                duration: 2,
              });
              handleClear();
              fetchServiceabilityList(1, 10);
            } else {
              const errorMsg =
                response?.data?.message || "Failed to save pincode serviceability";
              message.error({
                content: errorMsg,
                key: "saveLoading",
                duration: 2,
              });
            }
          })
          .catch((error) => {
            loadingMessage();
            const errorMsg =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to save pincode serviceability";
            message.error({
              content: errorMsg,
              key: "saveLoading",
              duration: 2,
            });
          });
      },
    });
  };

  const fetchServiceabilityList = (page, limit) => {
    setLoading(true);

    PincodeApi.getVayuQuickServiceability(page, limit)
      .then(({ response, status }) => {
        setLoading(false);
        if (status && response.data && response.data.status === 200) {
          setTableData(response.data.data.pincodesData);
          setTotalCount(response.data.data.count);
        } else {
          message.error({
            content:
              response?.data?.message || "Failed to fetch serviceability data",
            key: "fetch-error",
            duration: 2,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch serviceability data";
        message.error({
          content: errorMsg,
          key: "fetch-error",
          duration: 2,
        });
      });
  };

  const handleBookingOnlyToggle = (checked, record) => {
    setLoading(true);

    const payload = {
      pincode: parseInt(record.fromPincode),
      isBookingOnly: checked,
    };

    PincodeApi.updateVayuQuickServiceability(payload)
      .then(({ response, status }) => {
        setLoading(false);
        if (status && response.data && response.data.status === 200) {
          message.success({
            content:
              response.data.message || "Booking status updated successfully",
            key: "booking-update-success",
            duration: 2,
          });

          // Update the local data to reflect the change
          setTableData(
            tableData.map((item) => {
              if (
                item.fromPincode === record.fromPincode &&
                item.toPincode === record.toPincode
              ) {
                return { ...item, isBookingOnly: checked };
              }
              return item;
            })
          );
        } else {
          message.error({
            content:
              response?.data?.message || "Failed to update booking status",
            key: "booking-update-error",
            duration: 2,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update booking status";
        message.error({
          content: errorMsg,
          key: "booking-update-error",
          duration: 2,
        });
      });
  };

  const handleActiveToggle = async (checked, record) => {
    setLoading(true);
    const payload = {
      isActive: checked,
      fromPincode: parseInt(record.fromPincode),
      toPincode: parseInt(record.toPincode),
    };

    PincodeApi.updateVayuQuickServiceabilityActive(payload)
      .then(({ response, status }) => {
        setLoading(false);
        if (status && response.data && response.data.status === 200) {
          message.success(
            response?.data?.message || "Status updated successfully"
          );

          // Update the local data to reflect the change
          setTableData(
            tableData.map((item) => {
              if (item.fromPincode === record.fromPincode && item.toPincode === record.toPincode) {
                return { ...item, isActive: checked };
              }
              return item;
            })
          );
        } else {
          const errorMsg = response?.data?.message || "Failed to update status";
          message.error({
            content: errorMsg,
            key: "update-error",
            duration: 2,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update status";
        message.error({
          content: errorMsg,
          key: "update-error",
          duration: 2,
        });
      });
  };

  // Function to handle clear button
  const handleClear = () => {
    setSourcePincodes([]);
    setDestinationPincodes([]);
    setVayuQuick(false);
    setVayuQuickPro(false);
    setIsBookingOnly(false);
    message.info("Form cleared");
  };

  // Columns definition for source table
  const sourceColumns = [
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Remove">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveSourcePincode(record.pincode)}
              className="delete-button"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Columns definition for destination table
  const destinationColumns = [
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Remove">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveDestinationPincode(record.pincode)}
              className="delete-button"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Transform pincode arrays to table data format
  const sourceTableData = sourcePincodes.map((data, index) => ({
    key: `source-${index}`,
    pincode: data?.pincode || "",
    city: data?.city || "",
    state: data?.state || "",
  }));

  const destinationTableData = destinationPincodes.map((data, index) => ({
    key: `dest-${index}`,
    pincode: data?.pincode || "",
    city: data?.city || "",
    state: data?.state || "",
  }));

  const customEmptyText = (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
  );

  return (
    <div className="pincode-serviceability-container">
      <div className="back-button-section">
        <Heading title="VayuQuick PIN Code Mapping" />
      </div>

      <div className="main-content">
        <div className="input-section">
          <span className="pincode-label">Service Type:</span>
        </div>
        <div className="checkbox-row">
          <Checkbox
            checked={vayuQuick}
            onChange={(e) => setVayuQuick(e.target.checked)}
            className="checkbox-item"
          >
            VayuQuick
          </Checkbox>
          <Checkbox
            checked={vayuQuickPro}
            onChange={(e) => setVayuQuickPro(e.target.checked)}
            className="checkbox-item"
          >
            VayuQuick Pro
          </Checkbox>
        </div>

        <Row gutter={24} className="pincode-row">
          {/* Left Section - Source Pincodes */}
          <Col span={12}>
            <div className="input-section">
              <span className="pincode-label">Source Pincode:</span>
              <PincodeInput
                pincodes={sourcePincodes}
                onChange={handleSourcePincodeChange}
                isBookingOnly={isBookingOnly}
                setIsBookingOnly={setIsBookingOnly}
              />
            </div>

            <Card className="pincode-card">
              <Table
                columns={sourceColumns}
                dataSource={sourceTableData}
                pagination={false}
                className="pincode-table"
                size="middle"
                locale={{ emptyText: customEmptyText }}
                scroll={{ y: 240 }}
              />
            </Card>
          </Col>

          {/* Right Section - Destination Pincodes */}
          <Col span={12}>
            <div className="input-section">
              <div className="destination-header">
                <span className="pincode-label">Destination Pincode:</span>
              </div>
              <PincodeInput
                pincodes={destinationPincodes}
                onChange={handleDestinationPincodeChange}
                isBookingOnlyVisible={false}
              />
            </div>

            <Card className="pincode-card">
              <Table
                columns={destinationColumns}
                dataSource={destinationTableData}
                pagination={false}
                className="pincode-table"
                size="middle"
                locale={{ emptyText: customEmptyText }}
                scroll={{ y: 240 }}
              />
            </Card>
          </Col>
        </Row>

        <div className="button-section">
          <Button className="clear-button" onClick={handleClear}>
            Clear
          </Button>
          <Button type="primary" className="save-button" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>

      {/* Serviceability List Table */}
      <div className="serviceability-list-section">
        <Card className="serviceability-list-card">
          <ServiceabilityListTable
            loading={loading}
            tableData={tableData}
            pageSize={pageSize}
            totalCount={totalCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            handleBookingOnlyToggle={handleBookingOnlyToggle}
            handleActiveToggle={handleActiveToggle}
          />
        </Card>
      </div>
    </div>
  );
}

export default VayuQuickPincodeMapping;
